/**
 * PWA Service
 * Manages Progressive Web App features including service worker, push notifications,
 * app installation, and offline functionality
 */

import { logger } from '../utils/logger';

// PWA Installation interface
export interface PWAInstallPrompt {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

// Push subscription interface
export interface PushSubscriptionData {
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
}

// Notification permission states
export type NotificationPermission = 'default' | 'granted' | 'denied';

/**
 * PWA Service Class
 * Handles all PWA-related functionality
 */
class PWAService {
  private swRegistration: ServiceWorkerRegistration | null = null;
  private installPrompt: PWAInstallPrompt | null = null;
  private isOnline = navigator.onLine;
  private onlineCallbacks: Array<() => void> = [];
  private offlineCallbacks: Array<() => void> = [];

  constructor() {
    this.setupEventListeners();
  }

  /**
   * Initialize PWA service
   */
  async initialize(): Promise<void> {
    try {
      await this.registerServiceWorker();
      this.setupInstallPrompt();
      this.setupPushNotifications();
      logger.child('PWA').log('PWA Service initialized successfully');
    } catch (error) {
      logger.child('PWA').error('Failed to initialize PWA Service:', error);
    }
  }

  /**
   * Register service worker
   */
  async registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
    if (!('serviceWorker' in navigator)) {
      logger.child('PWA').warn('Service Worker not supported');
      return null;
    }

    try {
      // Since /sw.js does not exist on the server in this version,
      // we must programmatically unregister any existing service workers
      // to resolve stale ghost-worker caching issues in Chrome browsers.
      const registrations = await navigator.serviceWorker.getRegistrations();
      if (registrations.length > 0) {
        logger.child('PWA').log(`Evicting ${registrations.length} ghost service worker(s)...`);
        for (const registration of registrations) {
          const success = await registration.unregister();
          if (success) {
            logger.child('PWA').log('Ghost service worker unregistered successfully.');
          }
        }

        // Wipe caches to prevent outdated assets from blocking network fetches
        if ('caches' in window) {
          const cacheKeys = await caches.keys();
          for (const key of cacheKeys) {
            await caches.delete(key);
          }
          logger.child('PWA').log('Cache Storage cleared successfully.');
        }
      }
      return null;
    } catch (error) {
      logger.child('PWA').error('Service Worker eviction routine failed:', error);
      return null;
    }
  }

  /**
   * Update service worker
   */
  async updateServiceWorker(): Promise<void> {
    if (!this.swRegistration) return;

    try {
      await this.swRegistration.update();
      
      // Skip waiting and reload
      if (this.swRegistration.waiting) {
        this.swRegistration.waiting.postMessage({ type: 'SKIP_WAITING' });
        window.location.reload();
      }
    } catch (error) {
      logger.child('PWA').error('Failed to update service worker:', error);
    }
  }

  /**
   * Setup app installation prompt
   */
  private setupInstallPrompt(): void {
    window.addEventListener('beforeinstallprompt', (event) => {
      event.preventDefault();
      this.installPrompt = event as any;
      
      // Notify that app can be installed
      this.notifyInstallAvailable();
    });

    // Handle app installation
    window.addEventListener('appinstalled', () => {
      logger.child('PWA').log('PWA installed successfully');
      this.installPrompt = null;
      this.notifyInstallSuccess();
    });
  }

  /**
   * Check if app can be installed
   */
  canInstall(): boolean {
    return this.installPrompt !== null;
  }

  /**
   * Trigger app installation
   */
  async installApp(): Promise<boolean> {
    if (!this.installPrompt) {
      logger.child('PWA').warn('Install prompt not available');
      return false;
    }

    try {
      await this.installPrompt.prompt();
      const choiceResult = await this.installPrompt.userChoice;
      
      if (choiceResult.outcome === 'accepted') {
        logger.child('PWA').log('User accepted app installation');
        return true;
      } else {
        logger.child('PWA').log('User dismissed app installation');
        return false;
      }
    } catch (error) {
      logger.child('PWA').error('App installation failed:', error);
      return false;
    } finally {
      this.installPrompt = null;
    }
  }

  /**
   * Check if app is installed
   */
  isInstalled(): boolean {
    return window.matchMedia('(display-mode: standalone)').matches ||
           window.matchMedia('(display-mode: fullscreen)').matches ||
           (window.navigator as any).standalone === true;
  }

  /**
   * Setup push notifications
   */
  private async setupPushNotifications(): Promise<void> {
    if (!('Notification' in window) || !('PushManager' in window)) {
      logger.child('PWA').warn('Push notifications not supported');
      return;
    }

    // Check current permission
    const permission = Notification.permission;
    logger.child('PWA').log('Notification permission:', permission);
  }

  /**
   * Request notification permission
   */
  async requestNotificationPermission(): Promise<NotificationPermission> {
    if (!('Notification' in window)) {
      logger.child('PWA').warn('Notifications not supported');
      return 'denied';
    }

    try {
      const permission = await Notification.requestPermission();
      logger.child('PWA').log('Notification permission result:', permission);
      return permission as NotificationPermission;
    } catch (error) {
      logger.child('PWA').error('Failed to request notification permission:', error);
      return 'denied';
    }
  }

  /**
   * Subscribe to push notifications
   */
  async subscribeToPush(vapidPublicKey: string): Promise<PushSubscriptionData | null> {
    if (!this.swRegistration) {
      logger.child('PWA').error('Service Worker not registered');
      return null;
    }

    try {
      const appServerKey = this.urlBase64ToUint8Array(vapidPublicKey);

      const subscription = await this.swRegistration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: appServerKey,
      });

      const subscriptionData: PushSubscriptionData = {
        endpoint: subscription.endpoint,
        keys: {
          p256dh: this.arrayBufferToBase64(subscription.getKey('p256dh')!),
          auth: this.arrayBufferToBase64(subscription.getKey('auth')!),
        },
      };

      logger.child('PWA').log('Push subscription created:', subscriptionData);
      return subscriptionData;
    } catch (error) {
      logger.child('PWA').error('Failed to subscribe to push notifications:', error);
      return null;
    }
  }

  /**
   * Unsubscribe from push notifications
   */
  async unsubscribeFromPush(): Promise<boolean> {
    if (!this.swRegistration) return false;

    try {
      const subscription = await this.swRegistration.pushManager.getSubscription();
      if (subscription) {
        await subscription.unsubscribe();
        logger.child('PWA').log('Unsubscribed from push notifications');
        return true;
      }
      return false;
    } catch (error) {
      logger.child('PWA').error('Failed to unsubscribe from push notifications:', error);
      return false;
    }
  }

  /**
   * Show local notification
   */
  async showNotification(
    title: string,
    options: NotificationOptions = {}
  ): Promise<void> {
    if (!this.swRegistration) {
      logger.child('PWA').error('Service Worker not registered');
      return;
    }

    try {
      await this.swRegistration.showNotification(title, {
        icon: '/icon-192x192.png',
        badge: '/badge-72x72.png',
        ...options,
      });
    } catch (error) {
      logger.child('PWA').error('Failed to show notification:', error);
    }
  }

  /**
   * Cache URLs for offline access
   */
  async cacheUrls(urls: string[]): Promise<void> {
    if (!this.swRegistration) return;

    try {
      this.swRegistration.active?.postMessage({
        type: 'CACHE_URLS',
        data: { urls },
      });
    } catch (error) {
      logger.child('PWA').error('Failed to cache URLs:', error);
    }
  }

  /**
   * Store offline action for background sync
   */
  async storeOfflineAction(action: {
    type: string;
    data: any;
    url: string;
    method?: string;
  }): Promise<void> {
    if (!this.swRegistration) return;

    try {
      this.swRegistration.active?.postMessage({
        type: 'STORE_OFFLINE_ACTION',
        data: action,
      });
    } catch (error) {
      logger.child('PWA').error('Failed to store offline action:', error);
    }
  }

  /**
   * Check online status
   */
  isOnlineStatus(): boolean {
    return this.isOnline;
  }

  /**
   * Add online callback
   */
  onOnline(callback: () => void): void {
    this.onlineCallbacks.push(callback);
  }

  /**
   * Add offline callback
   */
  onOffline(callback: () => void): void {
    this.offlineCallbacks.push(callback);
  }

  /**
   * Remove online callback
   */
  removeOnlineCallback(callback: () => void): void {
    this.onlineCallbacks = this.onlineCallbacks.filter(cb => cb !== callback);
  }

  /**
   * Remove offline callback
   */
  removeOfflineCallback(callback: () => void): void {
    this.offlineCallbacks = this.offlineCallbacks.filter(cb => cb !== callback);
  }

  /**
   * Setup event listeners
   */
  private setupEventListeners(): void {
    // Online/offline events
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.onlineCallbacks.forEach(callback => callback());
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
      this.offlineCallbacks.forEach(callback => callback());
    });

    // Service worker messages
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        const { type, data } = event.data;
        
        switch (type) {
          case 'SYNC_SUCCESS':
            logger.child('PWA').log('Background sync completed:', data);
            break;
          default:
            logger.child('PWA').debug('Service Worker message:', type, data);
        }
      });
    }
  }

  /**
   * Notify about service worker update
   */
  public notifyServiceWorkerUpdate(): void {
    // This could trigger a UI notification
    logger.child('PWA').log('New version available! Please refresh.');
    
    // Dispatch custom event
    window.dispatchEvent(new CustomEvent('sw-update-available'));
  }

  /**
   * Notify about install availability
   */
  private notifyInstallAvailable(): void {
    logger.child('PWA').log('App can be installed');
    
    // Dispatch custom event
    window.dispatchEvent(new CustomEvent('pwa-install-available'));
  }

  /**
   * Notify about successful installation
   */
  private notifyInstallSuccess(): void {
    logger.child('PWA').log('App installed successfully');
    
    // Dispatch custom event
    window.dispatchEvent(new CustomEvent('pwa-install-success'));
  }

  /**
   * Convert VAPID key to Uint8Array
   */
  private urlBase64ToUint8Array(base64String: string): ArrayBuffer {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray.buffer;
  }

  /**
   * Convert ArrayBuffer to base64
   */
  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }
}

// Export singleton instance
export const pwaService = new PWAService();
export default pwaService;